import React, { ReactNode } from 'react';
import { HistoryUniversal, QuarterLoaderUniversal } from '@duxton-web/icons';
import { Typography } from 'antd';
import { produce } from 'immer';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';

import CanaryHistoryModal from './CanaryHistory/CanaryHistoryModal';
import { CanaryStatusSection } from './CanaryStatus';
import { CanaryRollout, transformCanaryResponse } from './CanaryStatus/types';
import { RolloutEmpty } from './RolloutEmpty';
import RolloutPendingApprovalMessage from './RolloutPendingApprovalMessage';
import Service, {
  Rollout,
  RolloutDiff,
  RolloutTarget,
  VariableInfo,
  VariableStatsView,
} from '@/lib/App';
import { Scope } from '@/lib/PlannerDTOs';
import { State } from '@/store/state';
import { fetchLatestRollout } from '@/store/state/rollouts/actions';
import {
  latestRolloutSelector,
  rolloutViewSelector,
} from '@/store/state/rollouts/selectors';
import { fetchLatestVariableStats } from '@/store/state/variableStats/actions';
import { variableStatsSelector } from '@/store/state/variableStats/selectors';
import { appendEnvParam, useAppNavigate } from '@/utils/navigation';

import './RolloutTabV2.css';

import { ExperimentOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Tooltip } from '@duxton-web/ui';
import { PageHeaderTagProps } from '@web-platform/page-header';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';

import Changelog from '../Changelog/Changelog';
import { renderInfos, renderTags } from '../RolloutPage/RolloutPage';
import VariablesSearch, {
  SearchByConstraintsItem,
} from '../Search/Variables/VariablesSearch';
import EventTrackingV2 from './EventTracking/EventTrackingV2';
import MoreActionSection from './MoreActionSection/MoreActionSection';
import { isSliderConstraint } from './RolloutEditor/RolloutBlock';
import RolloutEditorV2 from './RolloutEditor/RolloutEditorV2';
import {
  TRACK_CONF_MIN_LIMIT,
  TRACK_LEVEL,
} from './RolloutEditor/RolloutTrackConf/RolloutTrackConf';
import SaveButtonSection from './SaveButtonSection/SaveButtonSection';
import {
  AccessControlWrapper,
  DataTypeIcon,
  ObjectDetailPage,
} from '@/common/components';
import { getProperlyInstrumentedFacetList } from '@/common/ConstraintsEditor/Constraint';
import { getDomainFamily } from '@/common/domains';
import { isProduction } from '@/common/environment';
import { validateValue } from '@/common/StaticValueInput/ajv';
import { VariableState } from '@/constants';
import {
  RolloutContextProvider,
  useRolloutContext,
} from '@/contexts/RolloutContext';
import {
  UserRolesContextConsumer,
  UserRolesContextType,
} from '@/contexts/UserRolesContext';
import { useVariable } from '@/contexts/VariableContext';
import { ALL_TENANT_CONFIGS, SyncToTenantButton } from '@/features/tenant-sync';
import GuardrailsTag from '@/features/variable/components/complexity/guardrails';
import RisksTag from '@/features/variable/components/complexity/risks';
import VariableSettings from '@/features/variable/settings';
import { fetchVariable, updateVariable } from '@/store/state/variables/actions';
import {
  buildAccessTargetFromVariable,
  PermissionUtils,
  UserAction,
} from '@/utils/accessControl';
import { getExternalUrl } from '@/utils/externalService';
import { getVariableStatsStartDate } from '@/utils/rollout';
import { getLoggedUserEmail, isAdmin } from '@/utils/user';

interface OwnProps {
  showChangelog?: boolean;
  showSearchByConstraints?: boolean;
  onEditRollout: () => void;
}

interface StateProps {
  latestRollout: Rollout;
  loadingLatestRollout: boolean;
  variableStats: VariableStatsView;
}

interface DispatchProps {
  fetchLatestRollout: (variableName: string) => Promise<void>;
  fetchLatestVariableStats: (variableName: string) => Promise<void>;
  updateVariable: (variable: VariableInfo) => Promise<void>;
  fetchVariable: (variableName: string) => Promise<void>;
}

type RolloutTabProps = OwnProps &
  StateProps &
  DispatchProps &
  // v6 navigation props
  Pick<UserRolesContextType, 'permissionUtils'> & {
    rolloutContext: ReturnType<typeof useRolloutContext>;
    variable: VariableInfo;
    navigate: ReturnType<typeof useAppNavigate>;
    location: ReturnType<typeof useLocation>;
  };

interface RolloutData {
  rollout: Rollout;
}

interface RolloutTabStates {
  showVariableDetailModal: boolean;
  canaryRollout: CanaryRollout | null;
  canaryLoading: boolean;
  canaryError?: string;
  canaryHistoryOpen: boolean;
}

export class RolloutTabV2 extends React.Component<
  RolloutTabProps,
  RolloutTabStates
> {
  state: RolloutTabStates = {
    showVariableDetailModal: false,
    canaryRollout: null,
    canaryLoading: false,
    canaryError: undefined,
    canaryHistoryOpen: false,
  };

  private canaryRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private canaryModalOpen = false;

  get rolloutContext() {
    return this.props.rolloutContext;
  }

  async fetchLatestRollout(): Promise<void> {
    const { fetchLatestVariableStats, fetchLatestRollout, variable } =
      this.props;
    const {
      setRollout,
      setIsPublished,
      setTotalEventCount,
      setPendingRollout,
      setPendingApprovals,
    } = this.rolloutContext;

    if (variable) {
      const externalUrl = getExternalUrl(variable);

      if (externalUrl) {
        window.open(externalUrl, '_blank');
        return;
      }
    }

    if (variable?.state !== VariableState.ARCHIVED) {
      try {
        const data = (await fetchLatestRollout(
          variable.name,
        )) as unknown as RolloutData;

        let rollout: Rollout = data?.rollout;
        this.enhancedRollouts(rollout?.rollouts);
        if (!rollout?.rollouts) rollout['rollouts'] = [];

        setRollout(rollout);
        setIsPublished(true);
      } catch (e) {
        const defaultRollout = this.getDefaultRollout();
        setRollout(defaultRollout);
        console.warn(e);
      }

      try {
        const latestStats = (await fetchLatestVariableStats(
          variable.name,
        )) as any;
        const totalEventCount = Object.values(latestStats.scopes as Scope[])
          .map((scope) => scope.count)
          .reduce((prev, curr) => prev + curr, 0);

        setTotalEventCount(totalEventCount);
      } catch (e) {
        setTotalEventCount(0);
        console.warn(e);
      }

      let pendingRolloutDiffs: RolloutDiff[];
      try {
        pendingRolloutDiffs = await Service.planner.getPendingRolloutDiffs(
          variable.name,
        );

        // Check if pendingRolloutDiffs exists and has items before accessing
        if (pendingRolloutDiffs && pendingRolloutDiffs.length > 0) {
          const nextPendingRollout = produce(
            pendingRolloutDiffs[0].newRollout,
            (draft) => {
              draft?.rollouts?.forEach((rollout) =>
                Object.assign(rollout, {
                  constraints: rollout?.constraints || [],
                }),
              );
            },
          );

          setPendingApprovals(pendingRolloutDiffs);
          setPendingRollout(nextPendingRollout);
        } else {
          setPendingApprovals([]);
        }
      } catch (e) {
        setPendingApprovals([]);
        console.warn(e);
      }
    } else {
      const defaultRollout = this.getDefaultRollout();
      setRollout(defaultRollout);
      setTotalEventCount(0);
      setPendingApprovals([]);
    }
  }

  async fetchVariableStats(): Promise<void> {
    const { variable } = this.props;
    const { setProperlyInstrumentedFacetList } = this.rolloutContext;

    try {
      const res = await Service.planner.fetchStats(
        variable?.name,
        getVariableStatsStartDate(),
      );

      if (res) {
        setProperlyInstrumentedFacetList(
          getProperlyInstrumentedFacetList([res]) as string[],
        );
      }
    } catch (e) {
      setProperlyInstrumentedFacetList([]);
      console.error(e);
    }
  }

  async componentDidMount() {
    const { fetchVariable, variable } = this.props;
    this.fetchLatestRollout();
    this.fetchVariableStats();
    this.fetchCanaryStatus();
    fetchVariable(variable.name);
  }

  fetchCanaryStatus = async () => {
    const { variable } = this.props;

    this.setState({ canaryLoading: true });
    try {
      // Check whether canary is enabled and this variable's TF is enrolled.
      // pilot/tech-families returns [] when EnableCanaryRollout=false, so an
      // empty list (or a list that doesn't include this TF) means canary is
      // not active for this variable — skip the status fetch entirely.
      const canaryConfig = await Service.planner.getCanaryPilotTechFamilies();
      const isTFEligible =
        canaryConfig?.usePilotTechFamilies === false ||
        !!canaryConfig?.techFamilies?.includes(variable.techFamily);
      if (!isTFEligible) {
        this.setState({ canaryRollout: null, canaryLoading: false });
        this.syncRefreshTimer(false);
        return;
      }

      const response = await Service.planner.getActiveCanaryRollout(
        variable.name,
      );
      if (response) {
        const canaryRollout = transformCanaryResponse(response);
        this.setState({ canaryRollout, canaryLoading: false });
        this.syncRefreshTimer(canaryRollout.status === 'active');
      } else {
        this.setState({ canaryRollout: null, canaryLoading: false });
        this.syncRefreshTimer(false);
      }
    } catch (e) {
      this.setState({
        canaryError: e?.message || 'Failed to fetch canary status',
        canaryLoading: false,
      });
      this.syncRefreshTimer(false);
    }
  };

  syncRefreshTimer = (shouldRun: boolean) => {
    if (shouldRun && !this.canaryRefreshTimer) {
      this.canaryRefreshTimer = setInterval(() => {
        // Skip refresh while user has a decision modal open — avoids losing
        // textarea focus mid-typing. The next tick will pick up fresh data.
        if (this.canaryModalOpen) return;
        // Refresh both canary status and latest rollout so both panels stay current.
        this.fetchCanaryStatus();
        this.fetchLatestRollout();
      }, 60_000);
    } else if (!shouldRun && this.canaryRefreshTimer) {
      clearInterval(this.canaryRefreshTimer);
      this.canaryRefreshTimer = null;
    }
  };

  componentWillUnmount() {
    if (this.canaryRefreshTimer) {
      clearInterval(this.canaryRefreshTimer);
      this.canaryRefreshTimer = null;
    }
  }

  handleCanaryAdvance = async (runId: number, reason: string) => {
    try {
      await Service.planner.makeCanaryDecision(
        runId,
        'advance',
        reason || 'User advanced from portal',
      );
      this.fetchCanaryStatus();
    } catch (e) {
      this.setState({ canaryError: e?.message });
    }
  };

  handleCanaryHold = async (runId: number, mins: number) => {
    try {
      await Service.planner.makeCanaryDecision(
        runId,
        'hold',
        'User held from portal',
        mins,
      );
      this.fetchCanaryStatus();
    } catch (e) {
      this.setState({ canaryError: e?.message });
    }
  };

  handleCanaryRollback = async (runId: number, reason: string) => {
    try {
      await Service.planner.makeCanaryDecision(
        runId,
        'rollback',
        reason || 'User rolled back from portal',
      );
      this.fetchCanaryStatus();
      this.fetchLatestRollout();
    } catch (e) {
      this.setState({ canaryError: e?.message });
    }
  };

  handleCanaryPromote = async (runId: number, reason: string) => {
    try {
      await Service.planner.makeCanaryDecision(
        runId,
        'promote',
        reason || 'User promoted from portal',
      );
      this.fetchCanaryStatus();
      this.fetchLatestRollout();
    } catch (e) {
      this.setState({ canaryError: e?.message });
    }
  };

  handleCanaryOverride = async (runId: number, reason: string) => {
    try {
      await Service.planner.makeCanaryDecision(
        runId,
        'override',
        reason || 'User overrode breach from portal',
      );
      this.fetchCanaryStatus();
    } catch (e) {
      this.setState({ canaryError: e?.message });
    }
  };

  componentDidUpdate(
    _prevProps: Readonly<RolloutTabProps>,
    _prevState: Readonly<RolloutTabStates>,
    _snapshot?: any,
  ): void {
    const { location } = this.props;
    const pathname = location.pathname;

    if (pathname.endsWith('/rollout') && this.state.showVariableDetailModal) {
      this.setState({
        showVariableDetailModal: false,
      });
    }
  }

  enhancedRollouts = (rollouts: Array<RolloutTarget>) => {
    if (!rollouts || !rollouts?.length) return;

    rollouts.forEach((rollout) =>
      Object.assign(rollout, {
        reorder: false,
        constraints: rollout?.constraints || [],
      }),
    );
  };

  enhanceRolloutsIndex = (rollouts: Array<RolloutTarget>) => {
    if (!rollouts || !rollouts?.length) return false;

    rollouts.forEach((rollout, index) =>
      Object.assign(rollout, {
        index,
      }),
    );
  };

  getInvalidRolloutTargets(): {
    ['invalidValues']: number[];
    ['inproperFacets']: number[];
  } {
    const { variable } = this.props;
    const { rollout, pendingApprovals, properlyInstrumentedFacetList } =
      this.rolloutContext;

    const invalidRolloutTargetsIndex: number[] = [];
    const rolloutTargetsUsingInproperInstrumentedFacets: number[] = [];

    if (pendingApprovals.length)
      return {
        invalidValues: invalidRolloutTargetsIndex,
        inproperFacets: rolloutTargetsUsingInproperInstrumentedFacets,
      };

    if (rollout) {
      const allTargets = [].concat([rollout.default], rollout.rollouts || []);

      for (let index = 0; index < allTargets.length; index += 1) {
        const rolloutTarget = allTargets[index];

        if (rolloutTarget?.constraints?.length && isProduction) {
          rolloutTarget?.constraints?.forEach((constraint) => {
            if (
              !isSliderConstraint(variable, rollout?.assignBy, constraint) &&
              constraint?.target &&
              !properlyInstrumentedFacetList.includes(constraint?.target)
            ) {
              rolloutTargetsUsingInproperInstrumentedFacets.push(index);
              return false;
            }
          });
        }

        if (validateValue(variable, rolloutTarget)?.length)
          invalidRolloutTargetsIndex.push(index);
      }
    }

    return {
      invalidValues: invalidRolloutTargetsIndex,
      inproperFacets: rolloutTargetsUsingInproperInstrumentedFacets,
    };
  }

  // These methods are now in context

  getDefaultRollout = (): Rollout => {
    const { name: variableName } = this.props.variable;
    return {
      variable: variableName,
      version: '0',
      trackConf: {
        mode: TRACK_LEVEL.TRACK_USING_SAMPLE_RATE,
        limit: TRACK_CONF_MIN_LIMIT,
      },
      rolloutBy: '',
      rolloutType: 'simple',
      rollouts: [],
      default: {
        type: 'simple',
        value: 0,
        string: '',
        constraints: [],
      },
      assignBy: '',
    };
  };

  renderRestoreDiscardPrompt() {
    const { handleDiscard, hasActiveRollout } = this.rolloutContext;
    const { latestRollout } = this.props;

    return (
      <div
        style={{
          marginBottom: '20px',
        }}
      >
        <Alert
          type="info"
          message={
            <Flex align="center">
              <Flex flex={'1 0'}>
                You have loaded a previous rollout version and we&nbsp;
                <span className="bold">haven&apos;t saved </span>
                &nbsp;it yet. You can save and rollout with or without any
                additional manual changes, or dicard?
              </Flex>
              <Button
                size="small"
                type="link"
                onClick={() =>
                  handleDiscard(
                    latestRollout,
                    this.getDefaultRollout(),
                    hasActiveRollout(latestRollout),
                  )
                }
              >
                Discard
              </Button>
            </Flex>
          }
        />
      </div>
    );
  }

  handleRestore = (rollout: Rollout) => {
    const { variable, navigate } = this.props;
    const { handleRestore } = this.rolloutContext;

    handleRestore(rollout);
    this.enhancedRollouts(rollout?.rollouts);
    navigate.navigateTo(
      `/${getDomainFamily()}/variables/${variable.name}/rollout`,
    );
  };

  handleEditButtonClicked = () => {
    this.props.onEditRollout();
    const { pendingRollout, setIsEditRolloutButtonClicked, setRollout } =
      this.rolloutContext;

    setIsEditRolloutButtonClicked(true);
    setRollout(pendingRollout);
  };

  handleCancelEditButtonClicked = () => {
    const { variable, navigate } = this.props;
    navigate.navigateTo(
      `/${getDomainFamily()}/variables/${variable.name}/rollout`,
      {
        replace: true,
      },
    );
  };

  historyPaneClose = () => {
    const { setShowHistoryPane } = this.rolloutContext;
    setShowHistoryPane(false);
  };

  renderRolloutTargets() {
    const { variable, latestRollout } = this.props;
    const { pendingApprovals, isEditRolloutButtonClicked, hasActiveRollout } =
      this.rolloutContext;

    const hasActiveRolloutResult = hasActiveRollout(latestRollout);
    const isVariableArchived = variable.state == VariableState.ARCHIVED;
    const isManagedByPlatform = Boolean(variable?.metadata?.managedByPlatform);
    const disableEditing =
      ((isVariableArchived || pendingApprovals.length > 0) &&
        !isEditRolloutButtonClicked) ||
      this.state.canaryRollout?.status === 'active';

    if (isEditRolloutButtonClicked || hasActiveRolloutResult) {
      return (
        <AccessControlWrapper
          action={UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE}
          techFamilies={[variable.techFamily]}
          accessTargets={[buildAccessTargetFromVariable(variable)]}
        >
          {(hasAccess) => (
            <RolloutEditorV2 disabled={disableEditing || !hasAccess} />
          )}
        </AccessControlWrapper>
      );
    }

    return (
      <RolloutEmpty
        isArchived={isVariableArchived}
        disableConfigureRollout={
          pendingApprovals.length > 0 || isManagedByPlatform
        }
        enableStartConfigure={this.rolloutContext.enableStartConfigure}
      />
    );
  }

  renderEvenTrackingSection = () => {
    const { variable } = this.props;
    const { pendingApprovals, isEditRolloutButtonClicked } =
      this.rolloutContext;

    const isVariableArchived = variable.state == VariableState.ARCHIVED;
    const disableEditing =
      ((isVariableArchived || pendingApprovals.length > 0) &&
        !isEditRolloutButtonClicked) ||
      this.state.canaryRollout?.status === 'active';

    return <EventTrackingV2 disabled={disableEditing} />;
  };

  renderVariableUnpublishedBanner = (): ReactNode => {
    const { showChangelog, showSearchByConstraints } = this.props;
    const { isPublished } = this.rolloutContext;

    if (isPublished || showChangelog || showSearchByConstraints) return false;

    return (
      <Alert
        showIcon
        type="warning"
        message="Your variable is not yet published"
        description="For your code to receive rollout values from GrabX, please set up your rollout targets, before clicking Validate and Rollout.
Otherwise, your variable will continue to read from the code default.
As a good practice, do not leave any rollout values as empty."
      />
    );
  };

  renderRolloutContent = () => {
    const { showChangelog, showSearchByConstraints, variable } = this.props;

    if (showChangelog) return <Changelog onRestore={this.handleRestore} />;

    if (showSearchByConstraints) {
      const data: SearchByConstraintsItem = {
        name: variable.name,
        productEntity: variable?.productEntity,
      };

      return (
        <VariablesSearch
          hideTitle
          hideFilter
          hideSearch={!variable?.productEntity}
          data={data}
          hidePadding
        />
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {this.renderVariableUnpublishedBanner()}
        <CanaryStatusSection
          canaryRollout={this.state.canaryRollout}
          isLoading={this.state.canaryLoading}
          error={this.state.canaryError}
          onAdvance={this.handleCanaryAdvance}
          onHold={this.handleCanaryHold}
          onRollback={this.handleCanaryRollback}
          onPromote={this.handleCanaryPromote}
          onOverride={this.handleCanaryOverride}
          onRefresh={() => {
            this.fetchCanaryStatus();
            this.fetchLatestRollout();
          }}
          onModalStateChange={(isOpen) => {
            this.canaryModalOpen = isOpen;
          }}
          showCanaryActiveWarning={true}
        />
        {this.renderRolloutTargets()}
        {this.renderEvenTrackingSection()}
      </div>
    );
  };

  renderObjectDetailDescription = () => {
    const { variable } = this.props;

    return (
      <div className="grab-x__flex-col">
        <div className="grab-x__flex-row-align-center">
          <span
            style={{
              color: '#3D3D3D',
              fontWeight: 600,
            }}
          >
            Description:&nbsp;
          </span>{' '}
          <Typography.Text>{variable.desc}</Typography.Text>
        </div>
      </div>
    );
  };

  renderActions = () => {
    const { variable, navigate } = this.props;

    return (
      <Flex gap={4} align="center">
        <Tooltip title="Canary run history">
          <Button
            shape="circle"
            size="small"
            type="secondary"
            icon={<ExperimentOutlined {...({} as any)} />}
            onClick={() => this.setState({ canaryHistoryOpen: true })}
          />
        </Tooltip>
        <Button
          shape="circle"
          size="small"
          type="secondary"
          icon={<HistoryUniversal />}
          onClick={() => {
            navigate.navigateTo(
              `/${getDomainFamily()}/variables/${variable.name}/rollout/changelog`,
            );
          }}
        />
        <CanaryHistoryModal
          variableName={variable.name}
          open={this.state.canaryHistoryOpen}
          onClose={() => this.setState({ canaryHistoryOpen: false })}
        />
      </Flex>
    );
  };

  renderPrimaryActions = () => {
    const { variable } = this.props;

    return [
      <VariableSettings key="variable-setting" variableName={variable?.name} />,
      this.renderActions(),
    ];
  };

  renderSecondaryActions = (permissionUtils: PermissionUtils) => {
    const { variable, latestRollout, location } = this.props;
    const match = {
      url: `/${getDomainFamily()}/variables/${variable?.name}/rollout`,
    } as any;
    const { rollout, isPublished, hasPendingChanges } = this.rolloutContext;

    const hasPendingChangesResult = hasPendingChanges(latestRollout);
    const { invalidValues, inproperFacets } = this.getInvalidRolloutTargets();
    const isVariableArchived = variable.state == VariableState.ARCHIVED;
    const showingLatest = location.pathname === match.url;

    let latestRolloutClone = null;
    let rolloutClone = null;

    if (latestRollout) {
      latestRolloutClone = JSON.parse(JSON.stringify(latestRollout));
      this.enhanceRolloutsIndex(latestRolloutClone?.rollouts);
    }

    if (rollout) {
      rolloutClone = JSON.parse(JSON.stringify(rollout));
      this.enhanceRolloutsIndex(rolloutClone?.rollouts);
    }

    const items = [];

    const isManagedByPlatform = Boolean(variable?.metadata?.managedByPlatform);
    const isCanaryActive = this.state.canaryRollout?.status === 'active';
    const hasUnsavedChanges = hasPendingChangesResult || !isPublished;

    // Tenant sync buttons and Validate/Rollout button share the same permission requirements
    const canEditRollout =
      !isVariableArchived &&
      !isManagedByPlatform &&
      permissionUtils.canPerformAction(
        UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
        [variable.techFamily],
      );

    if (canEditRollout) {
      // Tenant sync buttons - disabled when user has unsaved changes or canary is active
      ALL_TENANT_CONFIGS.forEach((config) => {
        items.push(
          <SyncToTenantButton
            key={`sync-to-${config.name}`}
            config={config}
            variableName={variable?.name}
            disabled={hasUnsavedChanges || isCanaryActive}
          />,
        );
      });

      // Validate and Rollout button
      items.push(
        <SaveButtonSection
          invalidRolloutTargetsIndex={invalidValues}
          inproperInstrumentedFacetsIndex={inproperFacets}
          initialRollout={latestRolloutClone}
          disabled={!hasUnsavedChanges || isCanaryActive}
        />,
      );
    }

    if (isAdmin())
      items.push(<MoreActionSection showingLatest={!!showingLatest} />);

    return items;
  };

  renderObjectDetailPage = (children) => {
    const {
      variable,
      showChangelog,
      showSearchByConstraints,
      permissionUtils,
    } = this.props;
    const { rollout } = this.rolloutContext;

    if (!rollout || !variable) {
      return <QuarterLoaderUniversal />;
    }

    let defaultCrumbs: ItemType[] = [];

    defaultCrumbs.push({
      title: 'Variables',
      path: appendEnvParam(`/${getDomainFamily()}/variables`),
    });

    if (showChangelog) {
      defaultCrumbs.push(
        {
          title: variable.name,
          path: appendEnvParam(
            `/${getDomainFamily()}/variables/${variable.name}/rollout`,
          ),
        },
        {
          title: 'Changelog',
          path: '',
        },
      );
    } else if (showSearchByConstraints) {
      defaultCrumbs.push(
        {
          title: variable.name,
          path: appendEnvParam(
            `/${getDomainFamily()}/variables/${variable.name}/rollout`,
          ),
        },
        {
          title: 'Search by Constraints',
          path: '',
        },
      );
    } else {
      defaultCrumbs.push({
        title: variable.name,
        path: '',
      });
    }

    return (
      <ObjectDetailPage
        className="grab-x__rolloutTab-page"
        icon={<DataTypeIcon dataType={variable.type} />}
        breadcrumbs={defaultCrumbs}
        description={variable.desc}
        headerTags={[
          ...renderTags(variable, permissionUtils),
          {
            content: <RisksTag variable={variable} />,
            bordered: false,
            style: { background: 'transparent', padding: 0, margin: 0 },
          } as PageHeaderTagProps,
          {
            content: (
              <GuardrailsTag
                variable={variable}
                onVariableSaved={() => this.props.fetchVariable(variable.name)}
              />
            ),
            bordered: false,
            style: { background: 'transparent', padding: 0, margin: 0 },
          } as PageHeaderTagProps,
        ]}
        primaryActions={this.renderPrimaryActions()}
        secondaryActions={this.renderSecondaryActions(permissionUtils)}
        infos={renderInfos(variable)}
      >
        {children}
      </ObjectDetailPage>
    );
  };

  renderMainContent = () => {
    const { variable, permissionUtils, location } = this.props;
    const { pendingApprovals, isEditRolloutButtonClicked, showRestorePrompt } =
      this.rolloutContext;

    const userEmail = getLoggedUserEmail();
    const isVariableOwner = variable.owner == userEmail;
    const isPendingApprovalCreator =
      pendingApprovals.length > 0 &&
      pendingApprovals[0]?.createdBy === userEmail;
    const isShowingChangelogPage = location.pathname.includes('/changelog');

    return (
      <React.Fragment>
        {pendingApprovals.length > 0 && !isShowingChangelogPage && (
          <RolloutPendingApprovalMessage
            Diffs={pendingApprovals}
            isEditRolloutButtonClicked={isEditRolloutButtonClicked}
            handleEditButtonClicked={this.handleEditButtonClicked}
            handleCancelEditButtonClicked={this.handleCancelEditButtonClicked}
            isVariableOwner={isVariableOwner}
            isPendingApprovalCreator={isPendingApprovalCreator}
            disabled={
              variable.state === VariableState.ARCHIVED ||
              !permissionUtils.canPerformAction(
                UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
                [variable.techFamily],
              )
            }
          />
        )}
        {showRestorePrompt && this.renderRestoreDiscardPrompt()}
        {this.renderRolloutContent()}
      </React.Fragment>
    );
  };

  render() {
    const { variable } = this.props;
    const { rollout } = this.rolloutContext;

    if (!rollout || !variable) {
      return <QuarterLoaderUniversal />;
    }

    return this.renderObjectDetailPage(
      <div className="rollout_tab">
        <div className="rollout-content">
          <div className="rollout-col">{this.renderMainContent()}</div>
        </div>
      </div>,
    );
  }
}

function mapStateToProps(
  state: State,
  props: OwnProps & { variable: VariableInfo },
): StateProps {
  const { variable } = props;
  const rolloutViewState = rolloutViewSelector(state);
  const latestRollout = latestRolloutSelector(state, variable.name);
  const variableStats = variableStatsSelector(state, variable.name);

  return {
    latestRollout,
    loadingLatestRollout: rolloutViewState.loadingLatestRollout,
    variableStats: variableStats,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    fetchLatestRollout: ((variableName: string) =>
      dispatch(fetchLatestRollout(variableName))) as any,
    fetchLatestVariableStats: ((variableName: string) =>
      dispatch(fetchLatestVariableStats(variableName))) as any,
    updateVariable: (variable: VariableInfo) =>
      dispatch(updateVariable(variable)) as any,
    fetchVariable: ((variableName: string) =>
      dispatch(fetchVariable(variableName) as any)) as any,
  };
}

const ConnectedRolloutTab = connect<
  StateProps,
  DispatchProps,
  OwnProps & { variable: VariableInfo },
  State
>(
  mapStateToProps,
  mapDispatchToProps,
)(RolloutTabV2);

const Component = ConnectedRolloutTab;

// Bridge component to connect class component with context and navigation
const RolloutTabV2WithContext: React.FC<
  OwnProps & Pick<UserRolesContextType, 'permissionUtils'>
> = (props) => {
  const rolloutContext = useRolloutContext();
  const variable = useVariable();
  const navigate = useAppNavigate();
  const location = useLocation();

  return (
    <Component
      {...props}
      variable={variable}
      rolloutContext={rolloutContext}
      navigate={navigate}
      location={location}
    />
  );
};

export default function RolloutV2WithUserRolesContextProvider(props: OwnProps) {
  return (
    <RolloutContextProvider>
      <UserRolesContextConsumer>
        {({ permissionUtils }) => (
          <RolloutTabV2WithContext
            {...props}
            permissionUtils={permissionUtils}
          />
        )}
      </UserRolesContextConsumer>
    </RolloutContextProvider>
  );
}
